/**
 * KsefConfigWizard — Główny orchestrator wizarda (Krok 1-4 + Stepper + Next/Back).
 *
 * Pobiera dane konfiguracji z backendu, zarządza stanem kroków przez useKsefWizard,
 * renderuje aktywny krok i przyciski nawigacji.
 *
 * NIE jest jeszcze podpięty pod /ksef-config — dostępny TYLKO pod tymczasowym
 * route /ksef-config-preview-wizard.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { getAdminConfig, getSavedNip } from "./services/ksefApi";
import { useKsefWizard } from "./hooks/useKsefWizard";
import { DeleteConfigButton } from "./components/DeleteConfigButton";

import { Stepper } from "./wizard/Stepper";
import { WizardStep1Nip } from "./wizard/WizardStep1Nip";
import { WizardStep2Cert } from "./wizard/WizardStep2Cert";
import { WizardStep3Test } from "./wizard/WizardStep3Test";
import { WizardStep4Done } from "./wizard/WizardStep4Done";

export const KsefConfigWizard = ({ onFinished }) => {
  const navigate = useNavigate();

  const [adminConfig, setAdminConfig] = useState(null);
  const [savedNip, setSavedNip] = useState("");
  const [loading, setLoading] = useState(true);

  const {
    currentStep,
    completedSteps,
    canProceed,
    goNext,
    goBack,
    goTo,
    markCompleted,
  } = useKsefWizard(1);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgRes, nipRes] = await Promise.all([
        getAdminConfig().catch(() => ({ data: null })),
        getSavedNip().catch(() => ({ data: null })),
      ]);
      setAdminConfig(cfgRes.data || null);
      setSavedNip(nipRes.data?.company_nip || cfgRes.data?.company_nip || "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-skok: po każdym successful completion można od razu pójść do kolejnego kroku.
  // Decyzję pozostawiamy w handlerach poszczególnych kroków.
  const handleStep1Completed = useCallback(
    (newNip) => {
      setSavedNip(newNip);
      markCompleted(1);
    },
    [markCompleted]
  );

  const handleStep2Completed = useCallback(() => {
    markCompleted(2);
    // Po zapisie certu odśwież adminConfig, żeby krok 2 wiedział że cert jest
    fetchAll();
  }, [markCompleted, fetchAll]);

  const handleStep3Completed = useCallback(() => {
    markCompleted(3);
    markCompleted(4); // Krok 4 to tylko podsumowanie - od razu otwarty
  }, [markCompleted]);

  const handleFinish = () => {
    // Jeśli parent (KsefConfigPage) przekazał callback - on przeskoczy do trybu Tabsów.
    // Fallback: standalone preview → przejdź ręcznie do tabsów.
    if (onFinished) {
      onFinished();
    } else {
      navigate("/ksef-config-preview-tabs");
    }
  };

  const handleEdit = () => {
    goTo(1);
  };

  if (loading) {
    return (
      <Card className="border border-gray-200">
        <CardContent className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="ksef-config-wizard">
      <div>
        <h1 className="text-2xl font-semibold text-[#0F1F3D]">Konfiguracja KSeF</h1>
        <p className="text-sm text-gray-500 mt-1">
          Skonfiguruj połączenie z Krajowym Systemem e-Faktur w 4 krokach
        </p>
      </div>

      <Card className="border border-gray-200">
        <CardHeader className="border-b border-gray-100 pb-6">
          <Stepper
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={goTo}
          />
        </CardHeader>

        <CardContent className="pt-6 pb-6">
          {currentStep === 1 && (
            <WizardStep1Nip
              savedNip={savedNip}
              onCompleted={handleStep1Completed}
            />
          )}

          {currentStep === 2 && (
            <WizardStep2Cert
              adminConfig={adminConfig}
              savedNip={savedNip}
              onCompleted={handleStep2Completed}
              onChanged={fetchAll}
            />
          )}

          {currentStep === 3 && (
            <WizardStep3Test onCompleted={handleStep3Completed} />
          )}

          {currentStep === 4 && (
            <WizardStep4Done
              savedNip={savedNip}
              adminConfig={adminConfig}
              onFinish={handleFinish}
              onEdit={handleEdit}
            />
          )}

          {/* Navigation (ukryty na kroku 4 - tam są własne CTA) */}
          {currentStep < 4 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              <Button
                onClick={goBack}
                disabled={currentStep === 1}
                variant="outline"
                data-testid="wizard-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Wstecz
              </Button>

              <span className="text-xs text-gray-500">
                Krok {currentStep} z 4
              </span>

              <Button
                onClick={goNext}
                disabled={!canProceed}
                className={canProceed ? "" : "opacity-50 cursor-not-allowed"}
                data-testid="wizard-next-btn"
              >
                Dalej
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {!canProceed && currentStep < 4 && (
        <p className="text-center text-xs text-gray-500">
          {currentStep === 1 && "Zapisz NIP, aby przejść do następnego kroku"}
          {currentStep === 2 && "Wgraj i zapisz certyfikat, aby przejść do następnego kroku"}
          {currentStep === 3 && "Wykonaj poprawny test połączenia, aby przejść do następnego kroku"}
        </p>
      )}

      {/* Reset partial config — widoczny gdy backend pokazuje jakąkolwiek
          częściową konfigurację (NIP lub cert), ale wizard nie został ukończony.
          Pozwala wyzerować stan i zacząć od nowa. */}
      {(savedNip || adminConfig?.has_cert) && (
        <div className="pt-2">
          <p className="text-xs text-gray-500 mb-2">
            Masz częściową konfigurację z poprzedniej próby? Możesz ją usunąć i zacząć od początku:
          </p>
          <DeleteConfigButton
            hasConfig={!!savedNip || !!adminConfig?.has_cert}
            onDeleted={() => {
              setAdminConfig(null);
              setSavedNip("");
              fetchAll();
              goTo(1);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default KsefConfigWizard;
